import './styles.scss';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Spinner } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';

import {
  findSimilarCigarsDebug,
  fingerprintCigarImage,
  hydrateCigarCandidates,
  hydrateSimilarityCandidates,
  identifyCigarDebug,
  type IdentifyResearchMode,
  preprocessCigarImage,
  recognizeCigarFingerprint,
  recognizeCigarText,
} from '@/api/cigar-matching';
import Button from '@/components/overrides/button';
import { uploadToS3 } from '@/utils/imageUtils';

export type MatchFlowSourceType =
  | 'match_request'
  | 'recognition_test'
  | 'upload';

export interface MatchFlowSource {
  image: string;
  originalImage?: string;
  sourceType: MatchFlowSourceType;
  sourceId?: string | number;
  existingMatch?: unknown;
}

interface MatchFlowModalProps {
  isOpen: boolean;
  source: MatchFlowSource | null;
  onClose: () => void;
}

type StepKey =
  | 'image'
  | 'preprocess'
  | 'similar'
  | 'text'
  | 'fingerprint'
  | 'fingerprintRecognition'
  | 'identify';
type StepStatus = 'idle' | 'processing' | 'complete' | 'skipped' | 'failed';
type StageState = {
  status: StepStatus;
  result?: any;
  error?: string;
};

type IdentifyStageKey = 'similar' | 'text' | 'fingerprint';

const EMPTY_IDENTIFY_SELECTION = (): Record<IdentifyStageKey, boolean> => ({
  similar: false,
  text: false,
  fingerprint: false,
});

const rawStageResult = (result: any) => {
  if (!result) return undefined;
  const { hydratedCandidates, ...rawResult } = result;
  return rawResult;
};

const textCandidates = (result: any): any[] =>
  result?.textCandidates || result?.text_candidates || [];

const STEPS: Array<{
  key: StepKey;
  label: string;
  cost: string;
}> = [
  { key: 'image', label: 'Image', cost: 'Source' },
  { key: 'preprocess', label: 'Isolate', cost: 'Free' },
  { key: 'similar', label: 'Similar', cost: 'Free · Embeddings' },
  { key: 'text', label: 'Text', cost: 'Paid · Vision' },
  { key: 'fingerprint', label: 'Fingerprint', cost: 'Paid · Vision' },
  {
    key: 'fingerprintRecognition',
    label: 'Recognize',
    cost: 'Free · Database',
  },
  { key: 'identify', label: 'Identify', cost: 'Paid · AI' },
];

const EMPTY_STAGES = (): Record<StepKey, StageState> => ({
  image: { status: 'complete' },
  preprocess: { status: 'idle' },
  similar: { status: 'idle' },
  text: { status: 'idle' },
  fingerprint: { status: 'idle' },
  fingerprintRecognition: { status: 'idle' },
  identify: { status: 'idle' },
});

const errorMessage = (error: any) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  'This recognition stage failed.';

const percentage = (value: unknown) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return `${(number <= 1 ? number * 100 : number).toFixed(1)}%`;
};

const duration = (result: any) => {
  const milliseconds =
    result?.meta?.durationMs ??
    result?.metadata?.processing_ms ??
    result?.processing_ms;
  if (!Number.isFinite(Number(milliseconds))) return null;
  return Number(milliseconds) >= 1000
    ? `${(Number(milliseconds) / 1000).toFixed(1)} sec`
    : `${Number(milliseconds)} ms`;
};

const tokenUsage = (result: any) =>
  result?.meta?.tokenUsage ||
  result?.meta?.token_usage ||
  result?.metadata?.tokenUsage ||
  result?.metadata?.token_usage;

const estimatedCost = (result: any) => {
  const value =
    result?.meta?.estimatedCostUsd ??
    result?.meta?.estimated_cost_usd ??
    result?.metadata?.estimatedCostUsd ??
    result?.metadata?.estimated_cost_usd;
  return Number.isFinite(Number(value)) ? Number(value) : 0;
};

const formatCost = (value: number) =>
  value > 0 && value < 0.0001 ? `$${value.toFixed(6)}` : `$${value.toFixed(4)}`;

const uploadPreprocessedImage = async (image: string) => {
  if (!image.startsWith('data:')) return image;
  const response = await fetch(image);
  const blob = await response.blob();
  const id = uuidv4();
  const upload = await uploadToS3(
    blob,
    'cigars/match-debugger',
    `${id}-preprocessed.jpg`,
    id
  );
  return upload.data.media_url;
};

const values = (value: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((item: any) => item?.label || item?.name || String(item))
      .filter(Boolean);
  }
  return [String(value)];
};

const candidateLabel = (candidate: any) =>
  candidate?.cigarName ||
  candidate?.label ||
  candidate?.full_name ||
  candidate?.name ||
  candidate?.cigar?.full_name ||
  (candidate?.cigar_id || candidate?.cigarId
    ? `Cigar ${candidate.cigar_id || candidate.cigarId}`
    : 'Candidate');

const candidateScore = (candidate: any) =>
  percentage(
    candidate?.confidence ?? candidate?.score ?? candidate?.similarity
  );

const candidateImage = (candidate: any) =>
  candidate?.bandImageUrl ||
  candidate?.band_image_url ||
  candidate?.displayImageUrl ||
  candidate?.display_image_url ||
  candidate?.cigarImageUrl ||
  candidate?.imageUrl ||
  candidate?.image_url ||
  candidate?.imageId;

function MatchFlowModal({ isOpen, source, onClose }: MatchFlowModalProps) {
  const [activeStep, setActiveStep] = useState<StepKey>('image');
  const [stages, setStages] = useState(EMPTY_STAGES);
  const [workingImage, setWorkingImage] = useState('');
  const [workingImageType, setWorkingImageType] = useState<
    'original' | 'manual' | 'preprocessed'
  >('original');
  const [selectedCandidates, setSelectedCandidates] = useState<
    Partial<Record<StepKey, number>>
  >({});
  const [accepted, setAccepted] = useState<any>(null);
  const [identifySelection, setIdentifySelection] = useState(
    EMPTY_IDENTIFY_SELECTION
  );
  const [researchMode, setResearchMode] =
    useState<IdentifyResearchMode>('quick');
  const sessionSource = useRef(source);

  useEffect(() => {
    if (!isOpen || !source) return;
    sessionSource.current = source;
    setActiveStep('image');
    setStages(EMPTY_STAGES());
    setWorkingImage(source.image);
    setWorkingImageType(
      source.sourceType === 'upload' && source.originalImage !== source.image
        ? 'manual'
        : 'original'
    );
    setSelectedCandidates({});
    setIdentifySelection(EMPTY_IDENTIFY_SELECTION());
    setResearchMode('quick');
    setAccepted(null);
  }, [isOpen, source]);

  const sourceImage = source?.originalImage || source?.image || '';
  const stage = stages[activeStep];
  const activeIndex = STEPS.findIndex(({ key }) => key === activeStep);

  const clearDownstream = (step: StepKey) => {
    const start = STEPS.findIndex(({ key }) => key === step) + 1;
    setStages((current) => {
      const next = { ...current };
      STEPS.slice(start).forEach(({ key }) => {
        next[key] = { status: 'idle' };
      });
      return next;
    });
    setAccepted(null);
  };

  const selectWorkingImage = (
    image: string,
    type: 'original' | 'manual' | 'preprocessed'
  ) => {
    if (!image || image === workingImage) return;
    setWorkingImage(image);
    setWorkingImageType(type);
    clearDownstream('preprocess');
  };

  const runStage = async (key: StepKey) => {
    if (!workingImage || stages[key].status === 'processing') return;
    setActiveStep(key);
    setStages((current) => ({
      ...current,
      [key]: { ...current[key], status: 'processing', error: undefined },
    }));

    try {
      let result: any;
      if (key === 'preprocess') {
        result = await preprocessCigarImage(workingImage);
        if (result.image) {
          try {
            result.publicImageUrl = await uploadPreprocessedImage(result.image);
          } catch (uploadError) {
            result.uploadError = errorMessage(uploadError);
          }
        }
      }
      if (key === 'similar') {
        result = await findSimilarCigarsDebug(workingImage);
        result.hydratedCandidates = await hydrateSimilarityCandidates(
          result.candidates || []
        );
      }
      if (key === 'text') {
        result = await recognizeCigarText(workingImage);
        const ranked = [
          ...(result.proposedMatch
            ? [{ ...result.proposedMatch, proposed: true }]
            : []),
          ...textCandidates(result),
        ].filter(
          (candidate, index, all) =>
            all.findIndex(
              (item) =>
                (item.cigarId || item.cigar_id) ===
                (candidate.cigarId || candidate.cigar_id)
            ) === index
        );
        result.hydratedCandidates = await hydrateCigarCandidates(
          ranked.map((candidate) => ({
            ...candidate,
            cigar_id: candidate.cigar_id || candidate.cigarId,
          })),
          10
        );
      }
      if (key === 'fingerprint')
        result = await fingerprintCigarImage(workingImage);
      if (key === 'fingerprintRecognition') {
        const fingerprint = stages.fingerprint.result?.fingerprint;
        if (!fingerprint) throw new Error('Generate a fingerprint first.');
        result = await recognizeCigarFingerprint(fingerprint);
        result.hydratedCandidates = await hydrateCigarCandidates(
          (result.candidates || []).map((candidate: any) => ({
            ...candidate,
            cigar_id: candidate.cigar_id || candidate.cigarId,
          })),
          10
        );
      }
      if (key === 'identify') {
        const stageResults: Record<string, unknown> = {};
        if (identifySelection.similar && stages.similar.result) {
          stageResults.similar = rawStageResult(stages.similar.result);
        }
        if (identifySelection.text && stages.text.result) {
          stageResults.text = rawStageResult(stages.text.result);
        }
        if (identifySelection.fingerprint) {
          if (stages.fingerprint.result) {
            stageResults.fingerprint = rawStageResult(
              stages.fingerprint.result
            );
          }
          if (stages.fingerprintRecognition.result) {
            stageResults.recognize = rawStageResult(
              stages.fingerprintRecognition.result
            );
          }
        }
        result = await identifyCigarDebug(
          workingImage,
          stageResults,
          researchMode
        );
        const candidates = [result.match, ...(result.suggestions || [])].filter(
          Boolean
        );
        result.hydratedCandidates = await hydrateCigarCandidates(
          candidates,
          10
        );
        result.hydratedResearchCandidates = await hydrateCigarCandidates(
          result.research?.catalogCandidates || [],
          10
        );
      }

      setStages((current) => ({
        ...current,
        [key]: { status: 'complete', result },
      }));
      if (key === 'preprocess' && workingImageType === 'preprocessed') {
        if (result.publicImageUrl) setWorkingImage(result.publicImageUrl);
        else {
          setWorkingImage(source?.image || sourceImage);
          setWorkingImageType(
            source?.image !== sourceImage ? 'manual' : 'original'
          );
        }
      }
    } catch (error) {
      setStages((current) => ({
        ...current,
        [key]: {
          ...current[key],
          status: 'failed',
          error: errorMessage(error),
        },
      }));
    }
  };

  const skipPreprocess = () => {
    setStages((current) => ({
      ...current,
      preprocess: { ...current.preprocess, status: 'skipped' },
    }));
    setActiveStep('similar');
  };

  const candidates = useMemo(() => {
    const { result } = stage;
    if (!result) return [];
    if (result.hydratedCandidates?.length) return result.hydratedCandidates;
    if (activeStep === 'text') {
      const recognizedCandidates = textCandidates(result);
      if (recognizedCandidates.length) return recognizedCandidates;
      return (
        result.cigarCandidates ||
        result.cigar_candidates ||
        result.candidates ||
        []
      );
    }
    return result.candidates || [];
  }, [activeStep, stage.result]);

  const runUsage = useMemo(
    () =>
      STEPS.reduce(
        (total, { key }) => {
          const { result } = stages[key];
          const usage = tokenUsage(result);
          return {
            tokens:
              total.tokens +
              Number(usage?.totalTokens ?? usage?.total_tokens ?? 0),
            cost: total.cost + estimatedCost(result),
          };
        },
        { tokens: 0, cost: 0 }
      ),
    [stages]
  );

  const selectedCandidate = candidates[selectedCandidates[activeStep] || 0];
  const acceptCandidate = () => {
    if (!selectedCandidate) return;
    setAccepted({
      candidate: selectedCandidate,
      step: activeStep,
      confidence: candidateScore(selectedCandidate),
    });
  };

  const nextStep = () => {
    const next = STEPS[activeIndex + 1];
    if (!next) return;
    setActiveStep(next.key);
    if (stages[next.key].status === 'idle') runStage(next.key);
  };

  const renderMeta = (result: any, fallbackCost: string) => (
    <div className="match-flow-meta">
      <span>
        {result?.meta?.paidInference ? 'Paid inference' : fallbackCost}
      </span>
      {duration(result) && <span>{duration(result)}</span>}
      {(result?.meta?.provider || result?.metadata?.source) && (
        <span>{result.meta?.provider || result.metadata?.source}</span>
      )}
      {(result?.meta?.model || result?.metadata?.multimodal_model) && (
        <span>{result.meta?.model || result.metadata?.multimodal_model}</span>
      )}
      {tokenUsage(result) && (
        <span>
          {Number(
            tokenUsage(result).totalTokens ??
              tokenUsage(result).total_tokens ??
              0
          ).toLocaleString()}{' '}
          tokens ·{' '}
          {Number(
            tokenUsage(result).inputTokens ??
              tokenUsage(result).input_tokens ??
              0
          ).toLocaleString()}{' '}
          in /{' '}
          {Number(
            tokenUsage(result).outputTokens ??
              tokenUsage(result).output_tokens ??
              0
          ).toLocaleString()}{' '}
          out
        </span>
      )}
      {estimatedCost(result) > 0 && (
        <span>{formatCost(estimatedCost(result))} estimated</span>
      )}
      {(result?.meta?.costBasis || result?.meta?.cost_basis) && (
        <span>
          {(result.meta.costBasis || result.meta.cost_basis)
            .split('_')
            .join(' ')}
        </span>
      )}
    </div>
  );

  const renderCandidates = () => (
    <div className="match-flow-candidates">
      {!candidates.length && <p>No candidate identities were returned.</p>}
      {candidates.map((candidate: any, index: number) => (
        <button
          type="button"
          className={`match-flow-candidate${
            (selectedCandidates[activeStep] || 0) === index ? ' selected' : ''
          }`}
          key={`${candidate.cigar_id || candidate.cigarId || index}-${index}`}
          onClick={() =>
            setSelectedCandidates((current) => ({
              ...current,
              [activeStep]: index,
            }))
          }
        >
          {candidateImage(candidate) && (
            <img src={candidateImage(candidate)} alt="" />
          )}
          <span>
            <strong>{candidateLabel(candidate)}</strong>
            <small>
              {candidate.proposed && 'Proposed match · '}
              {candidateScore(candidate) || 'Score unavailable'}
              {(candidate.brand || candidate.cigar?.brand?.name) &&
                ` · ${candidate.brand || candidate.cigar.brand.name}`}
            </small>
            {values(candidate.evidence).length > 0 && (
              <em>{values(candidate.evidence).join(' · ')}</em>
            )}
          </span>
        </button>
      ))}
    </div>
  );

  const renderFingerprint = (result: any) => {
    const fingerprint = result?.fingerprint || {};
    const fields = [
      ['Colors', fingerprint.colors],
      ['Symbols', fingerprint.symbols],
      ['Motifs', fingerprint.motifs],
      ['Objects', fingerprint.objects],
      ['Typography', fingerprint.textStyle || fingerprint.typography],
      ['Monograms', fingerprint.monograms],
      ['Shape', fingerprint.shape],
    ];
    return (
      <div className="match-flow-fingerprint">
        {fields.map(([label, value]) =>
          values(value).length ? (
            <section key={String(label)}>
              <strong>{label}</strong>
              <div>
                {values(value).map((item) => (
                  <span key={item}>{item}</span>
                ))}
              </div>
            </section>
          ) : null
        )}
        {fingerprint.layout && (
          <section>
            <strong>Layout</strong>
            <p>
              {Object.values(fingerprint.layout).filter(Boolean).join(' · ')}
            </p>
          </section>
        )}
        {(fingerprint.description || fingerprint.artwork) && (
          <section>
            <strong>Description</strong>
            <p>{fingerprint.description || fingerprint.artwork}</p>
          </section>
        )}
      </div>
    );
  };

  const renderText = (result: any) => (
    <div className="match-flow-text-result">
      <section>
        <strong>Detected text</strong>
        <pre>{result.rawText || 'No text detected'}</pre>
      </section>
      {result.normalizedText && (
        <section>
          <strong>Normalized</strong>
          <p>{result.normalizedText}</p>
        </section>
      )}
      {[
        ['Tokens', result.tokens],
        ['Brands', result.brandCandidates || result.brand_candidates],
        ['Names', result.nameCandidates || result.name_candidates],
        ['Series', result.seriesCandidates || result.series_candidates],
        ['Aliases', result.aliases],
        ['Tags', result.tags || result.matchingTags],
        ['Other clues', result.otherClues || result.other_clues],
      ].map(([label, value]) =>
        values(value).length ? (
          <section key={String(label)}>
            <strong>{label}</strong>
            <div className="match-flow-chips">
              {values(value).map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </section>
        ) : null
      )}
      {percentage(result.confidence) && (
        <p>Confidence {percentage(result.confidence)}</p>
      )}
    </div>
  );

  const renderResearch = (result: any) => {
    const research = result?.research;
    if (!research?.attempted) return null;
    const hypotheses = research.hypotheses || [];
    const catalogCandidates = result.hydratedResearchCandidates?.length
      ? result.hydratedResearchCandidates
      : research.catalogCandidates || [];

    return (
      <div className="match-flow-research">
        <div className="match-flow-section-heading">
          <div>
            <h6>Research findings</h6>
            <span>
              {research.mode || 'quick'}
              {research.model ? ` · ${research.model}` : ''}
              {research.decisionReason
                ? ` · ${research.decisionReason.split('_').join(' ')}`
                : ''}
            </span>
          </div>
        </div>
        {research.summary && (
          <p className="match-flow-research-summary">{research.summary}</p>
        )}
        {hypotheses.length > 0 && (
          <section>
            <div className="match-flow-section-heading">
              <div>
                <h6>Potential new catalog entries</h6>
                <span>
                  Research hypotheses that are not linked to a database cigar
                </span>
              </div>
            </div>
            <div className="match-flow-hypotheses">
              {hypotheses.map((hypothesis: any, index: number) => (
                <article
                  key={`${hypothesis.brand}-${hypothesis.name}-${index}`}
                >
                  <header>
                    <div>
                      <strong>
                        {[hypothesis.brand, hypothesis.name]
                          .filter(Boolean)
                          .join(' · ') || 'Unidentified cigar'}
                      </strong>
                      {hypothesis.line && <small>{hypothesis.line}</small>}
                    </div>
                    {percentage(hypothesis.confidence) && (
                      <b>{percentage(hypothesis.confidence)}</b>
                    )}
                  </header>
                  {hypothesis.reasoning && <p>{hypothesis.reasoning}</p>}
                  {values(hypothesis.evidence).length > 0 && (
                    <ul>
                      {values(hypothesis.evidence).map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  )}
                  {values(hypothesis.visualTags).length > 0 && (
                    <div className="match-flow-chips">
                      {values(hypothesis.visualTags).map((tag) => (
                        <span key={tag}>{tag}</span>
                      ))}
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>
        )}
        {catalogCandidates.length > 0 && (
          <section>
            <div className="match-flow-section-heading">
              <div>
                <h6>Research catalog candidates</h6>
                <span>Existing database cigars found during research</span>
              </div>
            </div>
            <div className="match-flow-research-candidates">
              {catalogCandidates.map((candidate: any, index: number) => (
                <article
                  key={`${candidate.cigarId || candidate.cigar_id || index}-${index}`}
                >
                  {candidateImage(candidate) && (
                    <img src={candidateImage(candidate)} alt="" />
                  )}
                  <span>
                    <strong>{candidateLabel(candidate)}</strong>
                    <small>
                      {candidateScore(candidate) || 'Score unavailable'}
                      {candidate.brand ? ` · ${candidate.brand}` : ''}
                    </small>
                    {values(candidate.matchedBy).length > 0 && (
                      <em>
                        Matched by {values(candidate.matchedBy).join(' · ')}
                      </em>
                    )}
                  </span>
                </article>
              ))}
            </div>
          </section>
        )}
        {research.sources?.length > 0 && (
          <section className="match-flow-research-sources">
            <strong>Sources</strong>
            {research.sources.map((sourceItem: any) => (
              <a
                href={sourceItem.url}
                target="_blank"
                rel="noreferrer"
                key={sourceItem.url}
              >
                {sourceItem.title || sourceItem.url}
              </a>
            ))}
          </section>
        )}
      </div>
    );
  };

  const renderResult = () => {
    if (stage.status === 'processing') {
      return (
        <div className="match-flow-processing">
          <Spinner />
          <strong>Processing…</strong>
          <span>Completed results remain available from the steps above.</span>
        </div>
      );
    }
    if (stage.status === 'failed') {
      return (
        <div className="match-flow-error">
          <strong>Stage failed</strong>
          <span>{stage.error}</span>
        </div>
      );
    }
    if (stage.status === 'idle') {
      return (
        <div className="match-flow-empty">
          <strong>This stage has not run.</strong>
          <span>Run it when you are ready to inspect its output.</span>
        </div>
      );
    }
    if (stage.status === 'skipped') {
      return (
        <div className="match-flow-empty">
          <strong>Preprocessing skipped</strong>
          <span>Subsequent stages use the selected original/manual image.</span>
        </div>
      );
    }
    if (activeStep === 'image') return null;
    if (activeStep === 'preprocess') {
      const { result } = stage;
      return (
        <>
          <div className="match-flow-preprocess-output">
            {result?.image ? (
              <img src={result.image} alt="Automatically isolated cigar band" />
            ) : (
              <div>No safe crop was produced.</div>
            )}
            <div>
              <strong>
                Confidence {percentage(result?.confidence) || '—'}
              </strong>
              {result?.region && (
                <p>
                  Region: {result.region.x}, {result.region.y} ·{' '}
                  {result.region.width} × {result.region.height}
                </p>
              )}
              <p>{values(result?.transformations).join(' · ')}</p>
              {values(result?.notes).map((note) => (
                <p key={note}>{note}</p>
              ))}
              {result?.uploadError && (
                <p className="text-danger">
                  Processed-image upload failed: {result.uploadError}
                </p>
              )}
            </div>
          </div>
          {renderMeta(result, 'Free · Local')}{' '}
          {result?.publicImageUrl && (
            <Button
              color="primary"
              onClick={() =>
                selectWorkingImage(result.publicImageUrl, 'preprocessed')
              }
            >
              Use processed image
            </Button>
          )}
        </>
      );
    }
    if (activeStep === 'text')
      return (
        <>
          {renderText(stage.result)}
          <div className="match-flow-section-heading">
            <div>
              <h6>Text catalog candidates</h6>
              <span>
                Existing database cigars matched from the recognized text
              </span>
            </div>
          </div>
          {renderCandidates()}
          {renderMeta(stage.result, 'Paid · Vision')}
        </>
      );
    if (activeStep === 'fingerprint')
      return (
        <>
          {renderFingerprint(stage.result)}
          {renderMeta(stage.result, 'Paid · Vision')}
        </>
      );
    if (activeStep === 'identify')
      return (
        <>
          <div className="match-flow-section-heading">
            <div>
              <h6>Match and suggestions</h6>
              <span>Selectable results linked to existing database cigars</span>
            </div>
          </div>
          {renderCandidates()}
          {renderResearch(stage.result)}
          {renderMeta(stage.result, STEPS[activeIndex].cost)}
        </>
      );
    return (
      <>
        {renderCandidates()}
        {renderMeta(stage.result, STEPS[activeIndex].cost)}
      </>
    );
  };

  const identifyOptions: Array<{
    key: IdentifyStageKey;
    label: string;
    available: boolean;
  }> = [
    {
      key: 'similar',
      label: 'Similar',
      available: Boolean(stages.similar.result),
    },
    {
      key: 'text',
      label: 'Text',
      available: Boolean(stages.text.result),
    },
    {
      key: 'fingerprint',
      label: 'Fingerprint + Recognize',
      available: Boolean(
        stages.fingerprint.result || stages.fingerprintRecognition.result
      ),
    },
  ];

  if (!source) return null;

  return (
    <Modal isOpen={isOpen} toggle={onClose} className="match-flow-dialog" fade>
      <div className="modal-header match-flow-header">
        <div>
          <h5 className="modal-title">Cigar Match Flow</h5>
          <small>
            {source.sourceType.replace('_', ' ')}
            {source.sourceId !== undefined ? ` · ${source.sourceId}` : ''}
          </small>
        </div>
        <button type="button" className="close" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="match-flow-steps">
        {STEPS.map((item, index) => {
          const { status } = stages[item.key];
          return (
            <React.Fragment key={item.key}>
              <button
                type="button"
                className={`match-flow-step ${status}${item.key === activeStep ? ' active' : ''}`}
                onClick={() => setActiveStep(item.key)}
              >
                <span>
                  {status === 'processing' ? <Spinner size="sm" /> : index + 1}
                </span>
                <strong>{item.label}</strong>
                <small>{item.cost}</small>
              </button>
              {index < STEPS.length - 1 && <i>→</i>}
            </React.Fragment>
          );
        })}
      </div>
      <div className="modal-body match-flow-body">
        <aside className="match-flow-images">
          <div>
            <span>Original upload</span>
            <img src={sourceImage} alt="Original cigar" />
          </div>
          {source.image !== sourceImage && (
            <div>
              <span>Manual crop</span>
              <img src={source.image} alt="Manually cropped cigar" />
            </div>
          )}
          <div className="match-flow-working">
            <span>Working image · {workingImageType}</span>
            <img src={workingImage} alt="Current recognition input" />
          </div>
          <div className="match-flow-image-buttons">
            <Button
              size="sm"
              disabled={workingImage === sourceImage}
              onClick={() => selectWorkingImage(sourceImage, 'original')}
            >
              Use original
            </Button>
            {source.image !== sourceImage && (
              <Button
                size="sm"
                disabled={workingImage === source.image}
                onClick={() => selectWorkingImage(source.image, 'manual')}
              >
                Use manual crop
              </Button>
            )}
          </div>
        </aside>
        <main className="match-flow-content">
          <div className="match-flow-stage-heading">
            <div>
              <h5>{STEPS[activeIndex].label}</h5>
              <span>{STEPS[activeIndex].cost}</span>
            </div>
            {activeStep !== 'image' && (
              <Button
                size="sm"
                disabled={stage.status === 'processing'}
                onClick={() => {
                  clearDownstream(activeStep);
                  runStage(activeStep);
                }}
              >
                {stage.status === 'idle' ? 'Run Step' : 'Run Again'}
              </Button>
            )}
          </div>
          {activeStep === 'identify' && (
            <div className="match-flow-identify-controls">
              <fieldset className="match-flow-identify-options">
                <legend>Include previous results</legend>
                {identifyOptions.map(({ key, label, available }) => (
                  <label key={key}>
                    <input
                      type="checkbox"
                      checked={identifySelection[key] && available}
                      disabled={!available || stage.status === 'processing'}
                      onChange={(event) =>
                        setIdentifySelection((current) => ({
                          ...current,
                          [key]: event.target.checked,
                        }))
                      }
                    />
                    <span>{label}</span>
                    {!available && <small>Not run</small>}
                  </label>
                ))}
              </fieldset>
              <fieldset className="match-flow-identify-options">
                <legend>Research mode</legend>
                {(['quick', 'thorough'] as IdentifyResearchMode[]).map(
                  (mode) => (
                    <label key={mode}>
                      <input
                        type="radio"
                        name="match-flow-research-mode"
                        value={mode}
                        checked={researchMode === mode}
                        disabled={stage.status === 'processing'}
                        onChange={() => setResearchMode(mode)}
                      />
                      <span>{mode}</span>
                    </label>
                  )
                )}
              </fieldset>
            </div>
          )}
          {activeStep === 'image' && (
            <div className="match-flow-image-start">
              <h4>Choose how to begin</h4>
              <p>
                Keep the current image selected, optionally isolate the band, or
                skip directly to embedding similarity.
              </p>
              <div>
                <Button color="primary" onClick={() => runStage('preprocess')}>
                  Run preprocessing
                </Button>
                <Button onClick={skipPreprocess}>Skip to similarity</Button>
              </div>
            </div>
          )}
          {renderResult()}
          {stage.result && (
            <details className="match-flow-raw">
              <summary>Raw response</summary>
              <pre>{JSON.stringify(stage.result, null, 2)}</pre>
            </details>
          )}
        </main>
      </div>
      <div className="modal-footer match-flow-footer">
        <div>
          {accepted ? (
            <div className="match-flow-accepted">
              <strong>Accepted · {candidateLabel(accepted.candidate)}</strong>
              <span>
                Matched at{' '}
                {STEPS.find(({ key }) => key === accepted.step)?.label}
                {accepted.confidence ? ` · ${accepted.confidence}` : ''}
              </span>
            </div>
          ) : (
            <span className="text-muted">No result accepted for this run.</span>
          )}
          {(runUsage.tokens > 0 || runUsage.cost > 0) && (
            <span className="match-flow-run-usage">
              Run total: {runUsage.tokens.toLocaleString()} tokens ·{' '}
              {formatCost(runUsage.cost)} estimated
            </span>
          )}
        </div>
        <div>
          {candidates.length > 0 && activeStep !== 'fingerprint' && (
            <Button color="success" onClick={acceptCandidate}>
              Accept Match
            </Button>
          )}
          {activeStep !== 'image' &&
            activeIndex < STEPS.length - 1 &&
            stage.status !== 'processing' && (
              <Button color="primary" onClick={nextStep}>
                Next Step
              </Button>
            )}
          <Button color="light" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default MatchFlowModal;
